import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { Layout } from './components/layout/Layout';
import { GlobalModalProvider } from './context/GlobalModalContext';
import { GlobalModals } from './components/modals/GlobalModals';

// Eager — public entry, shown immediately on cold load
import { AuthPage } from './pages/AuthPage';

// Lazy — every other page becomes its own chunk, fetched on demand.
// Default landing /whos-warm is lazy too: the auth flow needs to resolve
// before we know to fetch it, so paying a tiny chunk-fetch cost there is
// fine, and the AuthPage doesn't carry its bundle around for nothing.
const OnboardingPage     = lazy(() => import('./pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const DashboardPage      = lazy(() => import('./pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const WhosWarmPage       = lazy(() => import('./pages/WhosWarmPage'));
// "Leads" and "Companies" were duplicate views of contacts/accounts — retired.
// Their routes redirect to the canonical pages (see below).
const AccountsPage       = lazy(() => import('./pages/AccountsPage').then(m => ({ default: m.AccountsPage })));
const ContactsPage       = lazy(() => import('./pages/ContactsPage').then(m => ({ default: m.ContactsPage })));
const OpportunitiesPage  = lazy(() => import('./pages/OpportunitiesPage').then(m => ({ default: m.OpportunitiesPage })));
const ContextPage        = lazy(() => import('./pages/ContextPage').then(m => ({ default: m.ContextPage })));
const CalendarPage       = lazy(() => import('./pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const MeetingsPage       = lazy(() => import('./pages/MeetingsPage'));
const MeetingDetailPage  = lazy(() => import('./pages/MeetingDetailPage'));
const SOWPage            = lazy(() => import('./pages/SOWPage').then(m => ({ default: m.SOWPage })));
const AnalyticsPage      = lazy(() => import('./pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const SettingsPage       = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const TermsPage          = lazy(() => import('./pages/TermsPage').then(m => ({ default: m.TermsPage })));
const PrivacyPage        = lazy(() => import('./pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));

function PageFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
    </div>
  );
}

function ProtectedRoute({ children, requireOnboarding = true }: { children: React.ReactNode; requireOnboarding?: boolean }) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to onboarding if not completed (unless already on onboarding page)
  const onboardingComplete = user?.settings?.onboardingComplete;
  if (requireOnboarding && !onboardingComplete && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <GlobalModalProvider>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<AuthPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          <Route
            path="/onboarding"
            element={
              <ProtectedRoute requireOnboarding={false}>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageFallback />}>
                    <Routes>
                      <Route path="/" element={<Navigate to="/whos-warm" replace />} />
                      <Route path="/whos-warm" element={<WhosWarmPage />} />
                      <Route path="/dashboard" element={<DashboardPage />} />
                      {/* Retired duplicate views → canonical pages */}
                      <Route path="/leads" element={<Navigate to="/contacts" replace />} />
                      <Route path="/companies" element={<Navigate to="/accounts" replace />} />
                      <Route path="/accounts" element={<AccountsPage />} />
                      <Route path="/accounts/:id" element={<AccountsPage />} />
                      <Route path="/contacts" element={<ContactsPage />} />
                      <Route path="/opportunities" element={<OpportunitiesPage />} />
                      <Route path="/context" element={<ContextPage />} />
                      <Route path="/calendar" element={<CalendarPage />} />
                      <Route path="/meetings" element={<MeetingsPage />} />
                      <Route path="/meetings/:id" element={<MeetingDetailPage />} />
                      <Route path="/sow" element={<SOWPage />} />
                      <Route path="/analytics" element={<AnalyticsPage />} />
                      <Route path="/settings" element={<SettingsPage />} />
                    </Routes>
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
      {/* Global modals render outside routes for proper stacking */}
      <GlobalModals />
    </GlobalModalProvider>
  );
}
